import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetBuildingsByIdQuery, useGetParkingSpotsQuery, useGetStatisticsVacancyQuery } from '@client/api';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardTitle,
    EmptyPlaceholder,
    isDefinedString,
    Pagination,
    Table,
    TableBody,
    TableHead,
    TableHeadCell,
    TableHeadRow,
    Text,
    Typography,
    useAreaMetricString,
} from '@client/toolkit';

import { BuildingParkingSpotsTableRow } from './building-parking-spots-table-row.component';

export interface BuildingParkingSpotsCardProps {
  className?: string;
}

export function BuildingParkingSpotsCard({ className }: BuildingParkingSpotsCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { buildingId } = useParams<{ buildingId: string }>();

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const { data: building, isFetching: isFetchingBuilding, isLoading: isLoadingBuilding } = useGetBuildingsByIdQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const {
    data: parkingSpotsData,
    isFetching: isFetchingParkingSpots,
    isLoading: isLoadingParkingSpots,
  } = useGetParkingSpotsQuery({
    'ancestorPath.Equal': building?.path,
    'pagination.Limit': pageSize,
    'pagination.Offset': pageOffset,
  }, { skip: !building?.path });

  const isLoading = isLoadingBuilding || isLoadingParkingSpots;
  const isFetching = isFetchingBuilding || isFetchingParkingSpots;

  const parkingSpots = useMemo(() => parkingSpotsData?.items ?? [], [parkingSpotsData]);

  const totalCount = parkingSpotsData?.totalCount ?? 0;

  const { data: vacancyReport, isFetching: isFetchingStatistics } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': building?.path,
      'discriminator.Contains': ['ParkingSpot'],
    },
    { skip: !building?.path },
  );

  const totalArea = useAreaMetricString(vacancyReport?.total?.area);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex-1">
          <CardTitle variant="dense">{tGlobal('entity.parkingSpot', { context: 'plural' })}</CardTitle>
          <Typography className="mt-2 -mb-1" variant="sub">
            {tCommon('totalEntity', { entity: tGlobal('parkingSpot.areaLower') })}
            <Text
              className="ml-1.5 font-bold text-white"
              isEmpty={!isDefinedString(totalArea)}
              loading={isFetchingStatistics || isFetchingBuilding}
              SkeletonProps={{ className: 'w-[46px]', inline: true }}
            >
              {totalArea}
            </Text>
          </Typography>
        </div>
        <CardActions className="min-h-[32px]" />
      </CardHeader>
      <CardContent variant="fill">
        <Table className="overflow-hidden rounded-md w-full" layout="fixed">
          {!isLoading && (parkingSpots != null && parkingSpots.length <= 0) && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>
                {tCommon('noEntitySpecified', { entity: tGlobal('entity.parkingSpot', { context: 'plural' }).toLowerCase() })}
              </EmptyPlaceholder>
            </caption>
          )}

          <TableHead>
            <TableHeadRow>
              <TableHeadCell className="w-48">{tGlobal('space.areaName')}</TableHeadCell>
              <TableHeadCell className="w-32">{tGlobal('parkingSpot.type')}</TableHeadCell>
              <TableHeadCell className="w-32">{tGlobal('parkingSpot.area')}</TableHeadCell>
              <TableHeadCell className="w-[40%]">{tGlobal('parkingSpot.description')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>

          <TableBody>
            {isLoading &&
              Array.from(Array(3).keys()).map((x) => (
                <BuildingParkingSpotsTableRow key={x} isFetching />
              ))}

            {!isLoading &&
              parkingSpots?.map((parkingSpot, index) => (
                <BuildingParkingSpotsTableRow
                  key={parkingSpot.id}
                  parkingSpot={parkingSpot}
                  isFetching={isFetching}
                />
              ))}
          </TableBody>
        </Table>

        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
      </CardContent>
    </Card>
  );
}
